import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { LanguageMode } from '../types';

export const useJsonValidation = (
  language: LanguageMode,
  disableJsonValidation: boolean,
  onChange?: (value: string, error: string | null) => void,
  onError?: (error: string | null) => void
) => {
  const [error, setError] = useState<string | null>(null);

  const debouncedValidation = useMemo(
    () =>
      debounce((newValue: string) => {
        // Only validate JSON
        if (language === 'json' && !disableJsonValidation) {
          try {
            JSON.parse(newValue);
            setError(null);
            onError?.(null);
            onChange?.(newValue, null);
          } catch (e) {
            const errorMessage =
              e instanceof Error ? e.message : 'Invalid JSON';
            setError(errorMessage);
            onError?.(errorMessage);
            onChange?.(newValue, errorMessage);
          }
        } else {
          // For markdown/text or disabled validation, no validation needed
          setError(null);
          onError?.(null);
          onChange?.(newValue, null);
        }
      }, 300),
    [onChange, onError, disableJsonValidation, language]
  );

  const handleChange = useCallback(
    (newValue: string) => {
      debouncedValidation(newValue);
    },
    [debouncedValidation]
  );

  useEffect(
    () => () => {
      debouncedValidation.cancel();
    },
    [debouncedValidation]
  );

  return { error, handleChange };
};
